import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppData } from '../data/apps';
import { getLocalizedAppStoreUrl } from '../utils/urlUtils';

interface AppCardProps {
    app: AppData;
    index: number;
}

const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const AppCard: React.FC<AppCardProps> = ({ app, index }) => {
    const { t, i18n } = useTranslation();
    const reduce = useReducedMotion();
    const storeUrl = getLocalizedAppStoreUrl(app.appStoreUrl, i18n.language);
    const name = t(`apps.${app.key}.name`);

    return (
        <motion.a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={reduce ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, delay: Math.min(index * 0.07, 0.21), ease: EASE }}
            className="group relative flex gap-4 rounded-[26px] border border-black/[0.06] bg-white p-5 outline-none transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-black/[0.1] hover:shadow-[0_30px_60px_-34px_rgba(15,23,42,0.45)] focus-visible:ring-2 focus-visible:ring-blue-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f5f5f7] active:translate-y-0 active:scale-[0.995] sm:gap-5 sm:p-6 dark:border-white/[0.08] dark:bg-[#131317] dark:hover:border-white/[0.16] dark:hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)] dark:focus-visible:ring-offset-[#0B0B0F]"
        >
            <div className="relative shrink-0">
                <span
                    aria-hidden="true"
                    className="absolute -inset-2 rounded-[24px] bg-blue-500/10 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-100"
                />
                <img
                    src={app.icon}
                    alt=""
                    width={80}
                    height={80}
                    loading="lazy"
                    decoding="async"
                    className="relative h-16 w-16 rounded-[18px] border border-black/[0.06] object-cover shadow-[0_10px_24px_-14px_rgba(15,23,42,0.6)] transition-transform duration-300 group-hover:scale-[1.04] sm:h-20 sm:w-20 sm:rounded-[22px] dark:border-white/10"
                />
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
                <div className="min-w-0">
                    <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-[-0.01em] text-gray-900 sm:text-[17px] dark:text-white">
                        {name}
                    </h3>
                    <p className="mt-1.5 text-[11px] font-medium tracking-[0.08em] text-gray-500 dark:text-gray-400">
                        {t(`apps.${app.key}.category`)}
                    </p>
                </div>

                <p className="mt-3 line-clamp-3 text-[13px] leading-relaxed text-gray-500 [text-wrap:pretty] sm:text-sm dark:text-gray-400">
                    {t(`apps.${app.key}.description`)}
                </p>

                <span className="mt-auto inline-flex items-center gap-1 pt-4 text-[13px] font-semibold text-blue-600 dark:text-blue-400">
                    {t('common.view_on_appstore')}
                    <ArrowUpRight
                        size={14}
                        className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                </span>
            </div>
        </motion.a>
    );
};

export default AppCard;
